import React, { useEffect, useState, useRef, useCallback, useContext } from "react";
import { Container, Row, Col, Button, Spinner, Modal, ModalBody } from "reactstrap";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../Context/AuthContext";
import { GetCatalogs } from "../../api/catalogService";
import FullScreenLoader from "../../components/Common/Loader";
import CommonToastContainer from "../../components/Common/CommonToastContainer";
import "../../assets/css/catalog.css";

const Catalogs = () => {
    document.title = "Catalogs";

    const { checkIsUserLoggedIn } = useContext(AuthContext);
    const isLoggedIn = checkIsUserLoggedIn();
    const navigate = useNavigate();
    const [progress, setProgress] = useState(0);
    const [showLoader, setShowLoader] = useState(false);
    const [allCatalogs, setAllCatalogs] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(1);
    const [imageLoaded, setImageLoaded] = useState({});
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [viewerSrc, setViewerSrc] = useState("");
    const [viewerTitle, setViewerTitle] = useState("");
    const [error, setError] = useState("");
    const [toasts, setToasts] = useState([]);
    const limit = 6;
    const observer = useRef();

    const showToast = (toastData) => {
        setToasts((prev) => [...prev, { id: Date.now(), ...toastData }]);
    };

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    };

    const fetchCatalogs = async () => {
        setShowLoader(true);
        try {
            const response = await GetCatalogs();
            const returnCode = response?.Message?.[0]?.RETURNCODE;
            const returnMessage = response?.Message?.[0]?.RETURNMESSAGE || "Failed to load catalogs.";

            if (returnCode === "0") {
                setAllCatalogs([]);
                setError(returnMessage);
                return;
            }
            const rows = Array.isArray(response?.CatalogList) ? response.CatalogList : [];
            if (returnCode === "1" && rows.length === 0) {
                setAllCatalogs([]);
                setError([]);
                return;
            }
            setAllCatalogs(rows);
            setError("");
        } catch (err) {
            console.error(err);
            setAllCatalogs([]);
            setError("Failed to load catalogs. Please try again later.");
        } finally {
            setProgress(100);
            setTimeout(() => {
                setShowLoader(false);
                setProgress(0);
            }, 300);
        }
    };

    useEffect(() => {
        if (!showLoader) return;
        let value = 10;
        setProgress(value);
        const interval = setInterval(() => {
            value = Math.min(value + Math.random() * 15, 90);
            setProgress(value);
        }, 400);

        return () => clearInterval(interval);
    }, [showLoader]);

    useEffect(() => {
        fetchCatalogs();
    }, []);

    const normalizeTitle = (title = "") => title.trim().toLowerCase();
    const catalogMap = new Map();

    allCatalogs.forEach((cat) => {
        const key = normalizeTitle(cat.WebDisplayName || cat.CatalogName || cat.Title);
        if (!catalogMap.has(key)) {
            catalogMap.set(key, { pdf: null, image: null });
        }

        const type = cat.AttachmentType?.toUpperCase();
        if (type === "PDF") catalogMap.get(key).pdf = cat;
        else if (type === "IMAGE") catalogMap.get(key).image = cat;
    });

    const mergedCatalogs = [];
    catalogMap.forEach(({ pdf, image }) => {
        if (pdf || image) {
            mergedCatalogs.push({
                ...(pdf || image),
                CoverImage: image?.AttachmentLink || null,
                PdfLink: pdf?.AttachmentLink || null,
            });
        }
    });

    const filteredCatalogs = mergedCatalogs.filter((cat) =>
        (cat.WebDisplayName || cat.Title || "").toLowerCase().includes(searchText.toLowerCase())
    );

    const totalPages = Math.ceil(filteredCatalogs.length / limit);
    const currentCatalogs = filteredCatalogs.slice(0, page * limit);

    const lastCardRef = useCallback(
        (node) => {
            if (showLoader) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && page < totalPages) {
                    setPage((prevPage) => prevPage + 1);
                }
            });
            if (node) observer.current.observe(node);
        },
        [showLoader, page, totalPages]
    );

    const validatePdf = async (url) => {
        try {
            const response = await fetch(url, { method: "HEAD" });
            return response.ok;
        } catch (error) {
            return false;
        }
    };

    const handleViewClick = async (cat) => {
        if (!isLoggedIn) {
            showToast({
                title: "Warning",
                message: "Please login to view or download our catalog.",
                type: "warning",
                autoClose: 1000,
            });
            return;
        }

        if (!cat.PdfLink) {
            showToast({
                message: "PDF file not available.",
                type: "warning",
            });
            return;
        }
        setShowLoader(true);
        const isValidPdf = await validatePdf(cat.PdfLink);
        setShowLoader(false);
        if (!isValidPdf) {
            showToast({
                title: "Error",
                message: "Something went wrong while loading the PDF.",
                type: "error",
            });
            return;
        }

        setViewerSrc(cat.PdfLink);
        setViewerTitle(cat.WebDisplayName || cat.Title);
        setIsViewerOpen(true);
    };

    return (
        <Container className="catalog-section px-2">
            <CommonToastContainer toasts={toasts} removeToast={removeToast} />
            {showLoader && <FullScreenLoader progress={progress}  />}

            <div className="catalog-header d-flex justify-content-between align-items-center mb-4">
                <h1 className="fw-bold text-primary">Explore Catalogs</h1>
                <input
                    type="text"
                    placeholder="Search Catalog..."
                    value={searchText}
                    onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
                    className="form-control rounded-pill"
                    style={{ maxWidth: 300 }}
                />
            </div>

            {!showLoader && currentCatalogs.length === 0 && error && (
                <div className="cart-empty-wrapper">
                    <div className="cart-empty-state text-center">
                        <img src="/assets/images/enquiry/noEnquiry.png" alt="No Catalogs" className="cart-empty-image mb-3" />
                        <h5 className="fw-semibold mb-2">To Unlock Catalog Connect To Your K.A.M</h5>
                        <p className="text-muted mb-4"> {error} </p>
                        <div className="d-flex justify-content-center gap-2">
                            <Link to="/vault-access" className="primary-button"> Go to Vault Access </Link>
                        </div>
                    </div>
                </div>
            )}

            <Row className="catalog-grid">
                {currentCatalogs.map((cat, idx) => {
                    const isLastItem = idx === currentCatalogs.length - 1;
                    const hasRealImage = Boolean(cat.CoverImage);
                    const fallbackImage = "/assets/images/noImage/no-image3.png";

                    return (
                        <Col key={cat.Detail_Id || idx} className="p-2 col-12 col-md-6 col-lg-4">
                            <div className="catalog-card" ref={isLastItem ? lastCardRef : null}>
                                <img
                                    src={hasRealImage ? cat.CoverImage : fallbackImage}
                                    alt={cat.WebDisplayName || "Catalog Image"}
                                    className="catalog-img"
                                    style={{ objectFit: hasRealImage ? "cover" : "contain", backgroundColor: "#f5f5f5" }}
                                    loading="lazy"
                                    onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; e.target.style.objectFit = "contain";}}
                                />
                                <div className="catalog-overlay">
                                    <h6 className="catalog-title">{cat.WebDisplayName}</h6>
                                    <Button color="primary" className="catalog-btn" onClick={() => handleViewClick(cat)}>
                                        VIEW PDF
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>

            {!isLoggedIn && (
                <div className="text-center my-4">
                    <h2 className="mb-2 text-primary catalog-before-login">
                        <a href="/register" className="text-primary fw-semibold cursor-pointer" onClick={(e) => { e.preventDefault(); navigate("/register"); }}>
                            Sign Up
                        </a>{" "}
                        or{" "}
                        <a href="/login" className="text-primary fw-semibold cursor-pointer" onClick={(e) => { e.preventDefault(); navigate("/login"); }}>
                            Login
                        </a> to get complete access to our latest catalogs.
                    </h2>
                </div>
            )}

            {page < totalPages && (
                <div className="text-center my-4">
                    <Spinner color="primary" />
                </div>
            )}

            <Modal isOpen={isViewerOpen} toggle={() => setIsViewerOpen(false)} size="xl" centered>
                <ModalBody className="p-0 position-relative">
                    <button className="viewer-close-icon" onClick={() => setIsViewerOpen(false)}>
                        &times;
                    </button>
                    <iframe
                        src={viewerSrc}
                        title={viewerTitle}
                        style={{ width: "100%", height: "95vh", border: "none" }}
                        onError={() => {
                            setIsViewerOpen(false);
                            showToast({
                                message: "Something went wrong while loading the PDF.",
                                type: "error",
                            });
                        }}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default Catalogs;
